import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import { FactureProforma, FactureProformaStatus } from '../../types/factureProforma';
import { User } from '../../types/auth';
import { Client } from '../../types/client';
import { facturesProformaAPI, userAPI, clientsAPI, auditLogsAPI } from '../../services/api';
import ColumnFilter from '../../components/ColumnFilter';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { FilterListOff as FilterListOffIcon } from '@mui/icons-material';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import PermissionButton from '../../components/PermissionButton';
import { extractErrorMessage } from '../../utils/errorHandler';
import usePdfPreview from '../../hooks/usePdfPreview';
import FactureProformaPreview from './FactureProformaPreview';
import FactureProformaFormDialog from './FactureProformaFormDialog';
// generateFactureProformaPDF is dynamically imported in the print handler so
// the @react-pdf/renderer chunk only loads when the user clicks Print.

const COMMERCIAL_COLOR = '#4CAF50';

const PROFORMA_FILTER_SCHEMA = {
  date:        { type: 'dateRange',    paramFrom: 'date_from', paramTo: 'date_to' },
  client_id:   { type: 'autocomplete', param: 'client_id', transform: (v: string) => Number(v) },
  reference:   { type: 'text',         param: 'reference' },
  designation: { type: 'text',         param: 'designation' },
  montant:     { type: 'numberRange',  paramMin: 'montant_min', paramMax: 'montant_max' },
  status:      { type: 'select',       param: 'status' },
  user_id:     { type: 'autocomplete', param: 'user_id', transform: (v: string) => Number(v) },
} as const;

const FactureProformaList: React.FC = () => {
  const [proformaList, setProformaList] = useState<FactureProforma[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedList, setDeletedList] = useState<FactureProforma[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [previewProforma, setPreviewProforma] = useState<FactureProforma | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editProformaId, setEditProformaId] = useState<number | undefined>(undefined);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const previewProformaPdf = (proforma: FactureProforma) => {
    openPreview({
      getBlob: async () => {
        const { getFactureProformaPDFBlob } = await import('../../utils/factureProformaPdfReactPdf');
        return getFactureProformaPDFBlob(proforma);
      },
      fileName: `${proforma.reference}.pdf`,
    });
    auditLogsAPI.logPdfDownload('FactureProforma', proforma.id, `Aperçu PDF - ${proforma.reference}`);
  };

  // Column filters
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    PROFORMA_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([
          userAPI.getUsers(0, 1000),
          clientsAPI.getAllForDropdown(),
        ]);
        setUsers(u);
        setClients(c);
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    })();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchProformas = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const { status, client_id, user_id, ...extra } = toQueryParams();
      const response = await facturesProformaAPI.getPaginated(
        skip,
        rowsPerPage,
        debouncedSearch || undefined,
        (status as string) || undefined,
        (user_id as number | undefined),
        (client_id as number | undefined),
        Object.keys(extra).length > 0 ? extra : undefined
      );
      setProformaList(response.items);
      setTotalCount(response.total);
    } catch (err) {
      showError('Erreur lors du chargement des factures proforma');
      console.error('Error fetching factures proforma:', err);
      setProformaList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const fetchDeleted = async () => {
    try {
      setLoading(true);
      const data = await facturesProformaAPI.getDeleted();
      setDeletedList(data);
    } catch (err) {
      showError('Erreur lors du chargement des factures proforma supprimées');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchProformas();
    }
  }, [fetchProformas, tabValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeleted();
    }
  };

  const handleDelete = async (proforma: FactureProforma) => {
    try {
      await facturesProformaAPI.delete(proforma.id);
      showSuccess('Facture proforma supprimée avec succès');
      fetchProformas();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleRestore = async (proforma: FactureProforma) => {
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer la facture proforma "${proforma.reference}" ?`,
      async () => {
        try {
          await facturesProformaAPI.restore(proforma.id);
          showSuccess('Facture proforma restaurée avec succès');
          fetchDeleted();
          fetchProformas();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      {
        title: 'Confirmer la restauration',
        confirmText: 'Restaurer',
        cancelText: 'Annuler',
      }
    );
  };

  const handleOpenNew = () => {
    setEditProformaId(undefined);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleOpenEdit = (id: number) => {
    setEditProformaId(id);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setFormMinimized(false);
    setEditProformaId(undefined);
  };

  const handleFormSuccess = () => {
    fetchProformas();
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case FactureProformaStatus.BROUILLON:
        return <Chip label="Brouillon" color="default" size="small" />;
      case FactureProformaStatus.VALIDEE:
        return <Chip label="Validée" color="primary" size="small" />;
      case FactureProformaStatus.ANNULEE:
        return <Chip label="Annulée" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDesignations = (proforma: FactureProforma): string => {
    if (!proforma.items || proforma.items.length === 0) return '-';
    return proforma.items.map((item) => item.designation).join(', ');
  };

  const getDesignationsForTooltip = (proforma: FactureProforma): React.ReactNode => {
    if (!proforma.items || proforma.items.length === 0) return '-';
    return (
      <Box>
        {proforma.items.map((item, index) => (
          <Box key={index}>• {item.designation}</Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', py: 3, px: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Gestion des Factures Proforma</Typography>
        {tabValue === 0 && (
          <PermissionButton
            moduleName="Commercial"
            operation="add"
            miniModuleName="Factures Proforma"
            variant="contained"
            onClick={handleOpenNew}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': { backgroundColor: '#388E3C' },
            }}
          >
            Nouvelle Facture Proforma
          </PermissionButton>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Factures Proforma Actives (${totalCount})`} />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          <Box display="flex" gap={2} mb={3}>
            <TextField
              placeholder="Rechercher par référence ou client..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 350 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {hasActive() && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={reset}
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                Effacer les filtres
              </Button>
            )}
          </Box>

          <TableContainer component={Paper} sx={getModuleTableTheme('commercial')}>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 100, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date
                      <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Client
                      <ColumnFilter
                        type="autocomplete"
                        value={filters.client_id}
                        onChange={(v) => update('client_id', v)}
                        label="Client"
                        color={COMMERCIAL_COLOR}
                        options={clients.map(c => ({ value: c.id.toString(), label: c.name }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 160, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      N° Proforma
                      <ColumnFilter type="text" value={filters.reference} onChange={(v) => update('reference', v)} label="référence" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Désignations
                      <ColumnFilter type="text" value={filters.designation} onChange={(v) => update('designation', v)} label="désignation" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 130, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Montant TTC
                      <ColumnFilter type="numberRange" value={filters.montant} onChange={(v) => update('montant', v)} label="montant" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 100, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      Statut
                      <ColumnFilter
                        type="select"
                        value={filters.status}
                        onChange={(v) => update('status', v)}
                        label="statut"
                        color={COMMERCIAL_COLOR}
                        options={[
                          { value: FactureProformaStatus.BROUILLON, label: 'Brouillon' },
                          { value: FactureProformaStatus.VALIDEE, label: 'Validée' },
                          { value: FactureProformaStatus.ANNULEE, label: 'Annulée' },
                        ]}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 150 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : proformaList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Aucune facture proforma trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  proformaList.map((proforma) => {
                    const designations = getDesignations(proforma);

                    return (
                      <TableRow key={proforma.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(proforma.date).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={proforma.client_name || ''} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {proforma.client_name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {proforma.reference}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={getDesignationsForTooltip(proforma)} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {designations}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {Number(proforma.total_ttc).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                          })}{' '}
                          DH
                        </TableCell>
                        <TableCell align="center">
                          {getStatusChip(proforma.status)}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => previewProformaPdf(proforma)}
                            title="Imprimer"
                          >
                            <PrintIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setPreviewProforma(proforma)}
                            title="Aperçu"
                          >
                            <ViewIcon />
                          </IconButton>
                          <PermissionButton
                            moduleName="Commercial"
                            operation="edit"
                            miniModuleName="Factures Proforma"
                            component="IconButton"
                            onClick={() => handleOpenEdit(proforma.id)}
                          >
                            <EditIcon />
                          </PermissionButton>
                          <PermissionButton
                            moduleName="Commercial"
                            operation="delete"
                            miniModuleName="Factures Proforma"
                            component="IconButton"
                            onClick={() => {
                              showConfirm(
                                `Êtes-vous sûr de vouloir supprimer la facture proforma "${proforma.reference}" ?`,
                                () => handleDelete(proforma),
                                {
                                  title: 'Confirmer la suppression',
                                  confirmText: 'Supprimer',
                                  cancelText: 'Annuler',
                                }
                              );
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </PermissionButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {tabValue === 1 && (
        <>
          <TableContainer component={Paper} sx={getModuleTableTheme('commercial')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>N° Proforma</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell align="right">Montant TTC</TableCell>
                  <TableCell align="center">Statut</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : deletedList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Aucune facture proforma supprimée
                    </TableCell>
                  </TableRow>
                ) : (
                  deletedList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((proforma) => (
                      <TableRow key={proforma.id}>
                        <TableCell>
                          {new Date(proforma.date).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>{proforma.reference}</TableCell>
                        <TableCell>{proforma.client_name}</TableCell>
                        <TableCell align="right">
                          {Number(proforma.total_ttc).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                          })}{' '}
                          DH
                        </TableCell>
                        <TableCell align="center">{getStatusChip(proforma.status)}</TableCell>
                        <TableCell>
                          {proforma.deleted_at
                            ? new Date(proforma.deleted_at).toLocaleDateString('fr-FR')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRestore(proforma)}
                            color="primary"
                            title="Restaurer"
                          >
                            <RestoreIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={deletedList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {previewProforma && (
        <FactureProformaPreview
          proforma={previewProforma}
          open={!!previewProforma}
          onClose={() => setPreviewProforma(null)}
          onPrint={() => {
            const p = previewProforma;
            setPreviewProforma(null);
            previewProformaPdf(p);
          }}
          onEdit={() => {
            setPreviewProforma(null);
            handleOpenEdit(previewProforma.id);
          }}
        />
      )}

      <FactureProformaFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSuccess={handleFormSuccess}
        proformaId={editProformaId}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default FactureProformaList;
